import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  CircularProgress,
  Box,
  Typography,
  Button,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

const ActivateAccount = () => {
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const theme = useTheme();
  const hasActivated = useRef(false);
  useEffect(() => {
    const activateAccount = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      if (hasActivated.current) return;
      hasActivated.current = true;

      if (!token) {
        setStatus("error");
        setMessage("No activation token provided.");
        return;
      }

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/auth/activate-account`,
          { params: { token } }
        );

        setStatus("success");
        setMessage(res.data.message || "Account activated successfully.");
      } catch (err) {
        console.error(err);
        setStatus("error");
        setMessage(
          err?.response?.data?.message ||
            "Activation failed. Please try again later."
        );
      }
    };

    activateAccount();
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: 4,
        textAlign: "center",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {status === "loading" && (
        <>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{ color: theme.palette.primary.main, mb: 3 }}
          />
          <Typography variant="body1" color="text.secondary">
            Activating your account...
          </Typography>
        </>
      )}

      {status === "success" && (
        <>
          <CheckCircleIcon
            sx={{
              fontSize: 60,
              color: theme.palette.success.main,
              mb: 3,
            }}
          />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {message}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            You can now log in to your account.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            href="/login"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: "bold",
            }}
          >
            Go to Login
          </Button>
        </>
      )}

      {status === "error" && (
        <>
          <ErrorIcon
            sx={{
              fontSize: 60,
              color: theme.palette.error.main,
              mb: 3,
            }}
          />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {message}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            If the problem persists, please contact support.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            href="/login"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: "bold",
            }}
          >
            Back to Login
          </Button>
        </>
      )}
    </Box>
  );
};

export default ActivateAccount;
